// The outreach SMS NUMBER endpoints (Jordan, 2026-10-26): Atlas texts patients from
// ONE shared Twilio number, set on Admin → Outreach; a practice may save an
// override on Practice Details → Integrations. Both screens read and write
// through this file and nothing else.
//
// Kept apart from ./api.js (the patient-facing surface) and ./templatesApi.js
// (templates): nothing here names a patient. The only phone numbers that pass
// through are the practice's / Atlas's own Twilio numbers and, for the test send,
// the staff member's own mobile, which the server echoes back masked.

const API_URL = process.env.REACT_APP_API_URL || "";

// The paths a Twilio Messaging Service must point at. The server fills in its
// public host (ATLAS_PUBLIC_URL); when it cannot, these paths are all we can show.
export const WEBHOOK_PATHS = {
  inboundSms: "/api/webhooks/twilio/sms",
  statusCallback: "/api/webhooks/twilio/status",
};

// Loose on purpose — the server is the validator (E.164: "+" then 8–15 digits; a
// bare 10-digit US number is accepted and stored as +1…). This only catches the
// typo that would otherwise cost a round trip: letters, a missing "+", too short.
export function looksLikePhone(text) {
  const compact = String(text || "").replace(/[\s().-]/g, "");
  return /^\+\d{8,15}$/.test(compact) || /^\d{10}$/.test(compact);
}

export function compactPhone(text) {
  return String(text || "").replace(/[\s().-]/g, "");
}

async function readError(response, fallback) {
  try {
    const body = await response.json();
    return { message: (body && body.error) || fallback, body: body || {} };
  } catch (err) {
    return { message: fallback, body: {} };
  }
}

async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const { message, body } = await readError(response, fallback);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== "object") {
    const error = new Error(
      "The response could not be read, so this may be incomplete. Try again.",
    );
    error.status = response.status;
    error.body = {};
    throw error;
  }
  return body;
}

function withBody(method, payload) {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
}

const str = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);

function normalizeWebhooks(raw) {
  const w = raw && typeof raw === "object" ? raw : {};
  return {
    inboundSms: str(w.inbound_sms),
    statusCallback: str(w.status_callback),
  };
}

// The fields both screens share: Twilio secrets present, dry run, the two URLs
// and whether a text would actually go out.
function normalizeStatus(body) {
  return {
    twilioConfigured: body.twilio_configured === true,
    dryRun: body.dry_run === true,
    smsConfigured: body.sms_configured === true,
    webhooks: normalizeWebhooks(body.webhooks),
  };
}

// --- Admin → Outreach: the shared number ------------------------------------

const adminBase = () => `${API_URL}/api/admin/outreach/sms`;

export function normalizeShared(body) {
  const b = body || {};
  return {
    sharedNumber: str(b.shared_number),
    ...normalizeStatus(b),
  };
}

// GET /api/admin/outreach/sms → {shared_number, twilio_configured, dry_run,
// sms_configured, webhooks}.
export async function fetchSharedSms() {
  const body = await request(adminBase(), {}, "Could not load the shared SMS number.");
  return normalizeShared(body);
}

// PUT /api/admin/outreach/sms {shared_number} → same shape. An empty string clears
// the number. 400 for a malformed number, with the server's sentence.
export async function saveSharedSms(sharedNumber) {
  const body = await request(
    adminBase(),
    withBody("PUT", { shared_number: compactPhone(sharedNumber) }),
    "Could not save the shared SMS number.",
  );
  return normalizeShared(body);
}

// POST /api/admin/outreach/sms/test {to} → {sent, dry_run, to_masked}. 400 for a
// bad `to`; 503 "SMS not configured" when the secrets or the number are missing.
// When dry run is on the server sends nothing and says so.
export async function sendSharedTestSms(to) {
  const body = await request(
    `${adminBase()}/test`,
    withBody("POST", { to: compactPhone(to) }),
    "Could not send the test message.",
  );
  return {
    sent: body.sent === true,
    dryRun: body.dry_run === true,
    toMasked: str(body.to_masked) || "the number you entered",
  };
}

// --- Practice Details → Integrations: the per-practice override ---------------

const practiceBase = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/outreach/sms-number`;

export function normalizeOverride(body) {
  const b = body || {};
  const p = b.phone && typeof b.phone === "object" ? b.phone : null;
  return {
    phone: p
      ? {
          id: p.id ?? null,
          phoneE164: str(p.phone_e164),
          label: str(p.label) || "",
          enabled: p.enabled !== false,
        }
      : null,
    sharedNumber: str(b.shared_number),
    // The override if one is saved, else the shared number; null when neither.
    effectiveNumber: str(b.effective_number),
    ...normalizeStatus(b),
  };
}

// GET …/outreach/sms-number → {phone|null, shared_number, effective_number, …}.
export async function fetchSmsNumber(practiceId) {
  const body = await request(
    practiceBase(practiceId),
    {},
    "Could not load the outreach SMS number.",
  );
  return normalizeOverride(body);
}

// PUT …/outreach/sms-number {phone_e164, label} → same shape. 400 malformed; 409
// when the number already belongs to another practice.
export async function saveSmsNumber(practiceId, { phoneE164, label }) {
  const body = await request(
    practiceBase(practiceId),
    withBody("PUT", { phone_e164: compactPhone(phoneE164), label: (label || "").trim() }),
    "Could not save the outreach SMS number.",
  );
  return normalizeOverride(body);
}

// DELETE …/outreach/sms-number → same shape with phone: null (the active row is
// disabled, not deleted; the practice falls back to the shared number).
export async function removeSmsNumber(practiceId) {
  const body = await request(
    practiceBase(practiceId),
    { method: "DELETE" },
    "Could not remove the outreach SMS number.",
  );
  return normalizeOverride(body);
}

// --- Status wording, shared by both screens -----------------------------------

export const DRY_RUN_LINE = "Dry run is on: nothing is sent (TWILIO_DRY_RUN)";

// One line that says whether a text would go out, and if not, why. `number` is
// the number that would be used (the shared one on Admin, the effective one on a
// practice card); `noNumberReason` is the screen's own wording for a missing one.
export function smsStatusLine({ twilioConfigured, smsConfigured, number }, noNumberReason) {
  if (!twilioConfigured) {
    return {
      ok: false,
      text: "SMS not configured: Twilio credentials are not set on the server",
    };
  }
  if (!number) {
    return { ok: false, text: `SMS not configured: ${noNumberReason}` };
  }
  if (!smsConfigured) {
    return { ok: false, text: "SMS not configured" };
  }
  return { ok: true, text: "SMS configured" };
}
